<script lang="ts">
	/**
	 * The "install the app" sheet, shown when the app is opened in a browser
	 * rather than from the home screen. See `$lib/pwa/install.ts` for when it
	 * appears, and why iPhone — and Android when Chrome sends no install event —
	 * gets steps instead of a button.
	 *
	 * A native <dialog> opened with showModal(): focus moves into it, Escape and
	 * «ليس الآن» both dismiss it for this visit, and the page behind is inert
	 * while it is up. It waits a moment after load so the first thing a person
	 * sees is the app, not a request.
	 */
	import { onMount } from 'svelte';

	import { asset } from '$app/paths';
	import {
		dismissInstall,
		onInstallOffer,
		promptInstall,
		type InstallOffer
	} from '$lib/pwa/install';
	import Icon from '$lib/ui/icons/Icon.svelte';

	const DELAY_MS = 1500;

	let dialog = $state<HTMLDialogElement | undefined>(undefined);
	let offer = $state<InstallOffer>('none');
	let ready = $state(false);

	onMount(() => {
		const timer = setTimeout(() => (ready = true), DELAY_MS);
		const stop = onInstallOffer((next) => (offer = next));
		return () => {
			clearTimeout(timer);
			stop();
		};
	});

	$effect(() => {
		if (!dialog) return;
		const show = ready && offer !== 'none';
		if (show && !dialog.open) dialog.showModal();
		if (!show && dialog.open) dialog.close();
	});

	function notNow(): void {
		dismissInstall();
	}

	async function install(): Promise<void> {
		await promptInstall();
		// Accepted or not, the browser has answered; don't ask again this visit.
		dismissInstall();
	}
</script>

<dialog
	bind:this={dialog}
	class="sheet"
	aria-labelledby="install-title"
	oncancel={(event) => {
		event.preventDefault();
		notNow();
	}}
>
	<div class="head">
		<img src={asset('/icons/icon-192.png')} alt="" width="56" height="56" />
		<div>
			<h2 id="install-title">ثبّت غُرَبَاء على جهازك</h2>
			<p class="muted">يُفتح كتطبيق من الشاشة الرئيسية، ويعمل دون إنترنت.</p>
		</div>
	</div>

	{#if offer === 'ios'}
		<ol class="steps" role="list">
			<li>
				<span class="num">١</span>
				<span>اضغط زر المشاركة <Icon name="share" size={1.2} label="المشاركة" /> في المتصفح</span>
			</li>
			<li>
				<span class="num">٢</span>
				<span
					>اختر «إضافة إلى الشاشة الرئيسية» <Icon
						name="add-square"
						size={1.2}
						label="إضافة إلى الشاشة الرئيسية"
					/></span
				>
			</li>
			<li>
				<span class="num">٣</span>
				<span>اضغط «إضافة»</span>
			</li>
		</ol>
		<p class="note">
			على الآيفون، التثبيت يحمي علاماتك أيضًا: قد يمسح Safari بيانات المواقع غير المثبّتة بعد أسبوع
			دون زيارة.
		</p>
		<div class="actions">
			<button type="button" class="primary" onclick={notNow}>حسنًا</button>
		</div>
	{:else if offer === 'manual'}
		<ol class="steps" role="list">
			<li>
				<span class="num">١</span>
				<span>افتح قائمة المتصفح <b class="glyph" aria-label="القائمة">⋮</b> أعلى الشاشة</span>
			</li>
			<li>
				<span class="num">٢</span>
				<span>اختر «تثبيت التطبيق» أو «إضافة إلى الشاشة الرئيسية»</span>
			</li>
			<li>
				<span class="num">٣</span>
				<span>اضغط «تثبيت»</span>
			</li>
		</ol>
		<div class="actions">
			<button type="button" class="primary" onclick={notNow}>حسنًا</button>
		</div>
	{:else}
		<div class="actions">
			<button type="button" class="primary" onclick={() => void install()}>تثبيت</button>
			<button type="button" class="quiet" onclick={notNow}>ليس الآن</button>
		</div>
	{/if}
</dialog>

<style>
	.sheet {
		inline-size: min(100% - 2 * var(--space-4), 28rem);
		margin-block: auto var(--space-4);
		padding: var(--space-6) var(--space-4) var(--space-4);
		border: 0;
		border-radius: var(--radius-lg);
		background-color: var(--bg-raised);
		color: var(--fg);
		box-shadow: var(--shadow-1);
	}

	.sheet::backdrop {
		background-color: var(--overlay);
	}

	.head {
		display: flex;
		align-items: center;
		gap: var(--space-3);
	}

	.head img {
		flex: none;
		border-radius: var(--radius-md);
	}

	h2 {
		margin: 0;
		font-size: var(--step-1);
		font-weight: 500;
	}

	.muted {
		margin: var(--space-1) 0 0;
		font-size: var(--step--1);
		color: var(--fg-muted);
	}

	.steps {
		list-style: none;
		margin: var(--space-4) 0 0;
		padding: 0;
		display: grid;
		gap: var(--space-3);
	}

	.steps li {
		display: flex;
		align-items: center;
		gap: var(--space-3);
	}

	.steps :global(.icon),
	.glyph {
		color: var(--accent);
	}

	.glyph {
		font-weight: 700;
		padding-inline: 0.15em;
	}

	.num {
		flex: none;
		display: grid;
		place-items: center;
		inline-size: 1.75rem;
		block-size: 1.75rem;
		border-radius: var(--radius-pill);
		background-color: var(--accent-muted);
		color: var(--accent);
		font-size: var(--step--1);
		font-weight: 500;
	}

	.note {
		margin: var(--space-4) 0 0;
		padding: var(--space-3);
		border-radius: var(--radius-md);
		background-color: var(--bg-sunken);
		font-size: var(--step--2);
		color: var(--fg-muted);
	}

	.actions {
		display: grid;
		gap: var(--space-2);
		margin-block-start: var(--space-4);
	}

	button {
		min-block-size: 2.75rem;
		border-radius: var(--radius-pill);
		font: inherit;
		cursor: pointer;
	}

	.primary {
		border: 0;
		background-color: var(--accent);
		color: var(--accent-fg);
		font-weight: 500;
	}

	.quiet {
		border: 0;
		background: none;
		color: var(--fg-muted);
	}

	button:focus-visible {
		outline: var(--focus-ring-width) solid var(--accent);
		outline-offset: var(--focus-ring-offset);
	}
</style>
